import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@freelancer/clipboard';
import { TrackingModule } from '@freelancer/tracking';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import {
  flFacebook,
  flInstagram,
  flLinkedin,
  flPinterest,
  flX,
  flYoutube,
  HoverColor,
  IconColor,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { FontWeight } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import {
  SocialButtonColor,
  SocialButtonSize,
  SocialButtonType,
  SocialPlatform,
  SocialPlatformConfig,
  SocialUrls,
} from './social-buttons.types';

@Component({
  standalone: true,
  selector: 'fl-social-buttons',
  imports: [
    ButtonComponent,
    IconModule,
    MatTooltipModule,
    NgTemplateOutlet,
    TrackingModule,
  ],
  template: `
    @for (platform of visiblePlatforms; track platform.key) {
      @if (trackingLabels[platform.key]; as label) {
        <div
          class="SocialButtonItem mr-xxxsmall"
          role="listitem"
          [flTrackingLabel]="label"
        >
          <ng-container [ngTemplateOutlet]="buttonContent"></ng-container>
        </div>
      } @else {
        <div
          class="SocialButtonItem mr-xxxsmall"
          role="listitem"
        >
          <ng-container [ngTemplateOutlet]="buttonContent"></ng-container>
        </div>
      }

      <ng-template #buttonContent>
        <fl-button
          [class]="'SocialButton SocialButton--' + platform.key"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="getLink(platform)"
          [color]="shareUrl ? ButtonColor.CUSTOM : undefined"
          [size]="shareUrl ? ButtonSize.MINI : undefined"
          [matTooltip]="platform.tooltip ?? ''"
          (click)="handleClick(platform.key)"
        >
          <fl-icon
            [label]="shareUrl ? platform.shareLabel : platform.linkLabel"
            [color]="getIconColor(platform)"
            [hoverColor]="getHoverColor(platform)"
            [name]="platform.icon"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
            [useIconFont]="platform.useIconFont ?? false"
            [fillIconFont]="platform.fillIconFont ?? false"
            [weight]="platform.weight ?? FontWeight.NORMAL"
          ></fl-icon>
        </fl-button>
      </ng-template>
    }
  `,
  styleUrls: ['./social-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideFlIcons({
      flFacebook,
      flX,
      flYoutube,
      flInstagram,
      flLinkedin,
      flPinterest,
    }),
  ],
})
export class SocialButtonsComponent implements OnChanges, OnInit {
  private toastAlertService = inject(ToastAlertService);
  private clipboard = inject(Clipboard);

  SocialButtonColor = SocialButtonColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  FontWeight = FontWeight;
  HoverColor = HoverColor;

  shareUrls: { [key: string]: string } = {};
  DEFAULT_SOCIAL_URLS: SocialUrls = {
    facebook: 'https://facebook.com/fansoffreelancer',
    x: 'https://x.com/freelancer',
    youtube: 'https://youtube.com/freelancerchannel',
    instagram: 'https://instagram.com/freelancerofficial',
  };
  socialUrls: SocialUrls = this.DEFAULT_SOCIAL_URLS;
  visiblePlatforms: readonly SocialPlatformConfig[] = [];

  readonly platforms: readonly SocialPlatformConfig[] = [
    {
      key: 'facebook',
      icon: 'ui-facebook',
      shareLabel: $localize`:Share link on Facebook social button:Share on Facebook`,
      linkLabel: $localize`:Facebook social button:Facebook`,
      mode: 'both',
    },
    {
      key: 'x',
      icon: 'ui-x',
      shareLabel: $localize`:Share link on X social button:Share on X`,
      linkLabel: $localize`:X social button:X`,
      mode: 'both',
      lightThemeColorOverride: true,
    },
    {
      key: 'youtube',
      icon: 'ui-youtube',
      shareLabel: '',
      linkLabel: $localize`:YouTube social button:YouTube`,
      mode: 'linkOnly',
    },
    {
      key: 'instagram',
      icon: 'ui-instagram',
      shareLabel: '',
      linkLabel: $localize`:Instagram social button:Instagram`,
      mode: 'linkOnly',
    },
    {
      key: 'linkedin',
      icon: 'ui-linkedin',
      shareLabel: $localize`:Share link on LinkedIn social button:Share on LinkedIn`,
      linkLabel: $localize`:LinkedIn social button:LinkedIn`,
      mode: 'both',
    },
    {
      key: 'pinterest',
      icon: 'ui-pinterest',
      shareLabel: $localize`:Share link on Pinterest social button:Share on Pinterest`,
      linkLabel: '',
      mode: 'shareOnly',
    },
    {
      key: 'copyLink',
      icon: 'ui-link-v2',
      shareLabel: $localize`:Copy link to clipboard social button:Copy link to clipboard`,
      linkLabel: '',
      mode: 'shareOnly',
      useIconFont: true,
      fillIconFont: true,
      weight: FontWeight.BOLD,
      tooltip: $localize`:Copy to clipboard tooltip message:Copy Link`,
    },
  ];

  @Input() color: SocialButtonColor = SocialButtonColor.FOREGROUND;
  @Input() shareUrl?: string;
  @Input() shareText?: string;
  @Input() size: SocialButtonSize = IconSize.MID;
  @Input() sizeTablet?: SocialButtonSize;
  @Input() sizeDesktop?: SocialButtonSize;

  @Input() excludePlatforms: readonly SocialPlatform[] = [];
  @Input() trackingLabels: Partial<Record<SocialPlatform, string>> = {};

  @Input() set customSocialUrls(value: SocialUrls) {
    this.socialUrls = value || this.DEFAULT_SOCIAL_URLS;
  }

  /** Maximize the width of the parent container */
  @HostBinding('attr.data-fluid')
  @Input()
  fluid?: boolean;

  @HostBinding('attr.data-type')
  type: SocialButtonType;

  @HostBinding('attr.role')
  role = 'list';

  ngOnChanges(): void {
    this.setType();
    this.createShareUrls();
    this.updateVisiblePlatforms();
  }

  ngOnInit(): void {
    this.setType();
    this.updateVisiblePlatforms();
  }

  setType(): void {
    this.type = this.shareUrl ? SocialButtonType.BUTTON : SocialButtonType.ICON;
  }

  createShareUrls(): void {
    const text = (this.shareText || '').split(' ').join('+');

    if (!this.shareUrl) {
      return;
    }

    this.shareUrls.facebook = `http://www.facebook.com/share.php?u=${this.shareUrl}`;
    this.shareUrls.x = `https://x.com/intent/tweet?text=${text}: ${this.shareUrl} via @freelancer Community`;
    this.shareUrls.linkedin = `http://www.linkedin.com/shareArticle?mini=true&url=${this.shareUrl}&title=${text}&source=https://www.freelancer.com/community`;
    this.shareUrls.pinterest = `https://pinterest.com/pin/create/bookmarklet/?url=${this.shareUrl}`;
    this.shareUrls.copyLink = this.shareUrl;
  }

  getLink(platform: SocialPlatformConfig): string | undefined {
    if (platform.key === 'copyLink') {
      return undefined;
    }
    if (this.shareUrl) {
      return this.shareUrls[platform.key];
    }
    return this.socialUrls[platform.key as keyof SocialUrls];
  }

  getIconColor(platform: SocialPlatformConfig): IconColor {
    if (
      !this.shareUrl &&
      platform.lightThemeColorOverride &&
      this.color === SocialButtonColor.LIGHT
    ) {
      return IconColor.LIGHT;
    }
    return IconColor.INHERIT;
  }

  getHoverColor(platform: SocialPlatformConfig): HoverColor {
    if (
      !this.shareUrl &&
      platform.lightThemeColorOverride &&
      this.color === SocialButtonColor.LIGHT
    ) {
      return HoverColor.LIGHT;
    }
    return HoverColor.CURRENT;
  }

  handleClick(platform: SocialPlatform): void {
    if (platform === 'copyLink') {
      this.copyToClipboard(this.shareUrl || '');
    }
  }

  private copyToClipboard(url: string): void {
    if (this.clipboard.copy(url)) {
      this.toastAlertService.open({
        message: $localize`Copied to clipboard.`,
        type: ToastAlertType.SUCCESS,
      });
    } else {
      this.toastAlertService.open({
        message: $localize`Copy to clipboard failed, please try again after adjusting your
      permissions.`,
        type: ToastAlertType.ERROR,
      });
    }
  }

  private updateVisiblePlatforms(): void {
    this.visiblePlatforms = this.platforms.filter(p => {
      if (this.excludePlatforms.includes(p.key)) {
        return false;
      }
      switch (p.mode) {
        case 'shareOnly':
          return !!this.shareUrl;
        case 'linkOnly':
          return !this.shareUrl && !!this.socialUrls[p.key as keyof SocialUrls];
        case 'both':
          return (
            !!this.shareUrl || !!this.socialUrls[p.key as keyof SocialUrls]
          );
        default:
          return false;
      }
    });
  }
}
